// components/settingsModal/ContactForm.js

import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  IconButton,
  ArrowBackIcon,
  Typography,
  InputAdornment,
  Visibility,
  VisibilityOff,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '../MuiComponents'; // Ensure Dialog components are exported in MuiComponents.js
import { Box, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '');
  const local = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (local.length <= 3) return local;
  if (local.length <= 6) return `(${local.slice(0, 3)}) ${local.slice(3)}`;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6, 10)}`;
};

const ContactForm = ({ contact, onSave, onCancel, onDelete, loading }) => {
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [pin, setPin] = useState('');
  const [notes, setNotes] = useState('');
  const [memberId, setMemberId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [contactZipCode, setContactZipCode] = useState('');

  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showMemberId, setShowMemberId] = useState(false);
  const [showCustomerId, setShowCustomerId] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // New state for delete dialog

  useEffect(() => {
    if (contact) {
      setContactName(contact.contact_name || '');
      setContactPhone(contact.contact_phone || '');
      setAccountNumber(contact.account_number || '');
      setLoginEmail(contact.login_email || '');
      setLoginPassword(contact.login_password || '');
      setPin(contact.pin || '');
      setNotes(contact.notes || '');
      setMemberId(contact.member_id || '');
      setCustomerId(contact.customer_id || '');
      setContactZipCode(contact.zip_code || '');
      // Auto-expand the "more info" section if any of its fields are populated
      // so users see their saved data without having to click the toggle.
      if (
        contact.login_email ||
        contact.login_password ||
        contact.pin ||
        contact.notes ||
        contact.member_id ||
        contact.customer_id ||
        contact.zip_code
      ) {
        setShowMoreInfo(true);
      }
    }
  }, [contact]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newContact = {
      contact_name: contactName,
      contact_phone: contactPhone,
      account_number: accountNumber,
      login_email: loginEmail,
      login_password: loginPassword,
      pin: pin,
      notes: notes,
      member_id: memberId,
      customer_id: customerId,
      zip_code: contactZipCode,
    };
    onSave(newContact);
  };
  
  const handleDelete = () => {
    onDelete(contact.id); // Pass the contact ID to the onDelete handler
  };

  return (
    <form onSubmit={handleSubmit}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        marginBottom: "10px",
      }}
      onClick={onCancel}
    >
      <ArrowBackIcon />
      <Typography variant="subtitle1" color="primary" style={{ marginLeft: "5px" }}>
        Back to Contacts
      </Typography>
    </div>
      <TextField
        label="Contact Name"
        value={contactName}
        onChange={(e) => setContactName(e.target.value)}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Contact Phone Number"
        value={formatPhoneNumber(contactPhone)}
        onChange={(e) => setContactPhone(e.target.value.replace(/\D/g, ''))}
        fullWidth
        margin="normal"
        placeholder="(512) 784-2279"
      />
      <TextField
        label="Account Number"
        type={showAccountNumber ? 'text' : 'password'} 
        value={accountNumber}
        onChange={(e) => setAccountNumber(e.target.value)}
        fullWidth
        margin="normal"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle account number visibility"
                onClick={() => setShowAccountNumber(!showAccountNumber)} 
                edge="end"
              >
                {showAccountNumber ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Progressive disclosure: extra IVR-specific fields the Phone Agent uses
          to navigate automated systems. Collapsed by default to keep the form
          short for users who only need name + phone + account_number. */}
      <Box
        onClick={() => setShowMoreInfo((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mt: 2,
          mb: 1,
          color: 'primary.main',
        }}
      >
        {showMoreInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        <Typography variant="subtitle2" style={{ marginLeft: '4px' }}>
          More info to help Maia navigate phone systems
        </Typography>
      </Box>
      <Collapse in={showMoreInfo} unmountOnExit>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Optional. Add any of these only if this company asks for them — Maia
          will use what you store and skip the rest.
        </Typography>
        <TextField
          label="Login Email Address"
          value={loginEmail}
          onChange={(e) => setLoginEmail(e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Login Password"
          type={showPassword ? 'text' : 'password'}
          value={loginPassword}
          onChange={(e) => setLoginPassword(e.target.value)}
          fullWidth
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Pin"
          type={showPin ? 'text' : 'password'}
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          fullWidth
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle pin visibility"
                  onClick={() => setShowPin(!showPin)}
                  edge="end"
                >
                  {showPin ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Member / Rewards ID"
          type={showMemberId ? 'text' : 'password'}
          value={memberId}
          onChange={(e) => setMemberId(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Used when an IVR asks for member or rewards number (separate from account number)."
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle member id visibility"
                  onClick={() => setShowMemberId(!showMemberId)}
                  edge="end"
                >
                  {showMemberId ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Customer ID"
          type={showCustomerId ? 'text' : 'password'}
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Used when an IVR asks specifically for a customer ID."
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle customer id visibility"
                  onClick={() => setShowCustomerId(!showCustomerId)}
                  edge="end"
                >
                  {showCustomerId ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="ZIP code on file with this contact"
          value={contactZipCode}
          onChange={(e) => setContactZipCode(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Only set this if the company has a different ZIP on file than your profile ZIP."
        />
        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          rows={4}
          margin="normal"
          helperText="Add any other information that may be useful for Maia to interact with this contact (no need to add any information already in your profile)."
        />
      </Collapse>

      {/* Save and Cancel Buttons */}
      <Button type="submit" variant="contained" color="primary" fullWidth>
        Save Contact
      </Button>
      <Button
        type="button"
        variant="outlined"
        color="primary"
        fullWidth
        onClick={onCancel}
        style={{ marginTop: '10px' }}
      >
        Cancel
      </Button>

      {/* Delete Contact Option */}
      <Button
        type="button"
        variant="text"
        color="error"
        fullWidth
        onClick={() => setDeleteDialogOpen(true)} // Open delete confirmation dialog
        style={{ marginTop: '20px' }}
      >
        Delete Contact
      </Button>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)} // Close the dialog
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this contact? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
          >
            Delete Contact
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
};

export default ContactForm;
